import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";

// Reusable composer. context = { party_id, policy_id, opportunity_id, renewal_id, claim_id, certificate_id, carrier_id, to }
export default function ComposeEmail({ open, onOpenChange, context = {}, onSent }) {
  const [f, setF] = useState({ to: "", cc: "", subject: "", body: "", template: "", reply_to: "" });
  const [partyId, setPartyId] = useState(context.party_id || "");
  const [parties, setParties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [forms, setForms] = useState([]);
  const [formQuery, setFormQuery] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [idem, setIdem] = useState("");
  const newKey = () => `compose-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    if (!open) return;
    setIdem(newKey());
    setF({ to: context.to || "", cc: "", subject: "", body: "", template: "", reply_to: "" });
    setAttachments(context.attachments || []); setPartyId(context.party_id || "");
    api.get("/email/templates").then(({ data }) => setTemplates(data.items || []));
    api.get("/parties?page_size=300").then(({ data }) => setParties(data.items || []));
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (partyId) {
      api.get(`/documents?party_id=${partyId}&page_size=100`).then(({ data }) => setDocs((data.items || []).filter((d) => d.dropbox_file_id)));
      const p = parties.find((x) => x.id === partyId);
      if (p && !f.to && p.email) setF((s) => ({ ...s, to: p.email }));
    } else setDocs([]);
  }, [partyId]); // eslint-disable-line

  const searchForms = async (v) => {
    setFormQuery(v);
    if (v.length < 2) { setForms([]); return; }
    const { data } = await api.get(`/forms?q=${encodeURIComponent(v)}&page_size=20`);
    setForms((data.items || []).filter((x) => x.dropbox_file_id && !x.is_client_copy));
  };

  const applyTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    const p = parties.find((x) => x.id === partyId) || {};
    const merge = { agency: "Endurance Casualty Insurance Agency", sender: "Endurance Casualty", name: p.display_name || "", client: p.display_name || "" };
    const render = (s) => (s || "").replace(/{{\s*(\w+)\s*}}/g, (_, k) => merge[k] || "");
    setF((s) => ({ ...s, template: key, subject: render(t.subject), body: render(t.body) }));
  };

  const toggleAttach = (type, id, filename) => {
    setAttachments((a) => a.find((x) => x.id === id) ? a.filter((x) => x.id !== id) : [...a, { type, id, filename }]);
  };

  const send = async (asDraft) => {
    if (!f.to.trim()) { toast.error("A recipient (To) is required."); return; }
    setBusy(true);
    try {
      const payload = {
        idempotency_key: idem, as_draft: asDraft,
        to: f.to.split(",").map((x) => x.trim()).filter(Boolean),
        cc: f.cc.split(",").map((x) => x.trim()).filter(Boolean),
        reply_to: f.reply_to, subject: f.subject, body: f.body, template: f.template,
        party_id: partyId, policy_id: context.policy_id || "", opportunity_id: context.opportunity_id || "",
        renewal_id: context.renewal_id || "", claim_id: context.claim_id || "",
        certificate_id: context.certificate_id || "", carrier_id: context.carrier_id || "",
        attachments,
      };
      await api.post("/email/compose", payload);
      toast.success(asDraft ? "Draft saved." : "Email queued for sending.");
      setIdem(newKey());
      onOpenChange(false); onSent && onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="compose-email-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Compose Email</DialogTitle>
          <DialogDescription className="text-xs text-mutedsea">Sent via Proton. Attach client documents or library forms directly — no download needed.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Related Client</Label>
              <Select value={partyId || "__none"} onValueChange={(v) => setPartyId(v === "__none" ? "" : v)}><SelectTrigger data-testid="compose-party" className="bg-white text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64"><SelectItem value="__none">— none —</SelectItem>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs mb-1 block">Template</Label>
              <Select value={f.template || "__none"} onValueChange={(v) => v !== "__none" && applyTemplate(v)}><SelectTrigger data-testid="compose-template" className="bg-white text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64"><SelectItem value="__none">— none —</SelectItem>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs mb-1 block">To *</Label><Input data-testid="compose-to" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="recipient@example.com, another@example.com" className="bg-white text-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Cc</Label><Input data-testid="compose-cc" value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} className="bg-white text-sm" /></div>
            <div><Label className="text-xs mb-1 block">Reply-To</Label><Input data-testid="compose-replyto" value={f.reply_to} onChange={(e) => setF({ ...f, reply_to: e.target.value })} className="bg-white text-sm" /></div>
          </div>
          <div><Label className="text-xs mb-1 block">Subject</Label><Input data-testid="compose-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="bg-white text-sm" /></div>
          <div><Label className="text-xs mb-1 block">Body</Label><Textarea data-testid="compose-body" rows={7} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="bg-white text-sm" /></div>

          <div className="border border-border rounded-sm p-3">
            <div className="text-xs font-semibold text-slate mb-2 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Attachments</div>
            {attachments.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{attachments.map((a) => (
              <Badge key={a.id} variant="outline" className="bg-ice text-atlantic border-sky/50" data-testid={`attach-chip-${a.id}`}>{a.filename}<button onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))} className="ml-1"><X className="h-3 w-3" /></button></Badge>
            ))}</div>}
            {partyId && docs.length > 0 && <div className="mb-2">
              <div className="text-[11px] text-mutedsea mb-1">Client documents</div>
              <div className="flex flex-wrap gap-1">{docs.map((d) => (
                <button key={d.id} data-testid={`attach-doc-${d.id}`} onClick={() => toggleAttach("document", d.id, d.filename)} className={`text-xs px-2 py-1 rounded-sm border ${attachments.find((a) => a.id === d.id) ? "bg-navy text-white border-navy" : "bg-white border-border text-slate hover:bg-ice"}`}>{d.filename}</button>
              ))}</div></div>}
            <div>
              <Input data-testid="attach-form-search" value={formQuery} onChange={(e) => searchForms(e.target.value)} placeholder="Search library forms to attach…" className="bg-white text-sm h-8" />
              {forms.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{forms.map((fm) => (
                <button key={fm.id} data-testid={`attach-form-${fm.id}`} onClick={() => toggleAttach("form", fm.id, `${fm.form_number || fm.name}.pdf`)} className={`text-xs px-2 py-1 rounded-sm border ${attachments.find((a) => a.id === fm.id) ? "bg-navy text-white border-navy" : "bg-white border-border text-slate hover:bg-ice"}`}>{fm.form_number || fm.name}</button>
              ))}</div>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" data-testid="compose-draft" onClick={() => send(true)} disabled={busy} className="border-atlantic text-atlantic">Save Draft</Button>
          <Button data-testid="compose-send" onClick={() => send(false)} disabled={busy} className="bg-navy hover:bg-atlantic text-white">{busy ? "Sending…" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
